import * as LabelPrimitive from "@radix-ui/react-label"
import { cn } from "@repo/ui/lib/utils"
import React from "react"

type SizeValue =
  | "6xl"
  | "5xl"
  | "4xl"
  | "3xl"
  | "2xl"
  | "xl"
  | "lg"
  | "m"
  | "s"
  | "xs"

type ResponsiveSize =
  | SizeValue
  | { default?: SizeValue; ipad?: SizeValue; desktop?: SizeValue }

type WeightValue = "regular" | "medium" | "semibold" | "bold"

type ResponsiveWeight =
  | WeightValue
  | { default?: WeightValue; ipad?: WeightValue; desktop?: WeightValue }

interface TypographyVariant {
  size?: ResponsiveSize
  weight?: ResponsiveWeight
}

interface Typography<T> extends React.HTMLAttributes<T>, TypographyVariant {}

const COMMON_CLASS_NAME = "text-foreground"
const COMMON_HEADING_STYLES = "leading-[1.2]"
const COMMON_BODY_STYLES = "leading-[1.5]"

const SIZE_CLASSES: Record<SizeValue, string> = {
  "6xl": "text-[32px]",
  "5xl": "text-[30px]",
  "4xl": "text-[28px]",
  "3xl": "text-[24px]",
  "2xl": "text-[20px]",
  xl: "text-[18px]",
  lg: "text-[16px]",
  m: "text-[14px]",
  s: "text-[12px]",
  xs: "text-[11px]",
}

const IPAD_SIZE_CLASSES: Record<SizeValue, string> = {
  "6xl": "ipad:text-[32px]",
  "5xl": "ipad:text-[30px]",
  "4xl": "ipad:text-[28px]",
  "3xl": "ipad:text-[24px]",
  "2xl": "ipad:text-[20px]",
  xl: "ipad:text-[18px]",
  lg: "ipad:text-[16px]",
  m: "ipad:text-[14px]",
  s: "ipad:text-[12px]",
  xs: "ipad:text-[11px]",
}

const DESKTOP_SIZE_CLASSES: Record<SizeValue, string> = {
  "6xl": "desktop:text-[32px]",
  "5xl": "desktop:text-[30px]",
  "4xl": "desktop:text-[28px]",
  "3xl": "desktop:text-[24px]",
  "2xl": "desktop:text-[20px]",
  xl: "desktop:text-[18px]",
  lg: "desktop:text-[16px]",
  m: "desktop:text-[14px]",
  s: "desktop:text-[12px]",
  xs: "desktop:text-[11px]",
}

const WEIGHT_CLASSES: Record<WeightValue, string> = {
  bold: "font-bold",
  semibold: "font-semibold",
  medium: "font-medium",
  regular: "font-normal",
}

const IPAD_WEIGHT_CLASSES: Record<WeightValue, string> = {
  bold: "ipad:font-bold",
  semibold: "ipad:font-semibold",
  medium: "ipad:font-medium",
  regular: "ipad:font-normal",
}

const DESKTOP_WEIGHT_CLASSES: Record<WeightValue, string> = {
  bold: "desktop:font-bold",
  semibold: "desktop:font-semibold",
  medium: "desktop:font-medium",
  regular: "desktop:font-normal",
}

function resolveWeightClasses(weight: ResponsiveWeight): string {
  if (typeof weight === "string") return WEIGHT_CLASSES[weight]
  return [
    weight.default !== undefined ? WEIGHT_CLASSES[weight.default] : "",
    weight.ipad !== undefined ? IPAD_WEIGHT_CLASSES[weight.ipad] : "",
    weight.desktop !== undefined ? DESKTOP_WEIGHT_CLASSES[weight.desktop] : "",
  ]
    .filter(Boolean)
    .join(" ")
}

function resolveSizeClasses(size: ResponsiveSize): string {
  if (typeof size === "string") return SIZE_CLASSES[size]
  return [
    size.default !== undefined ? SIZE_CLASSES[size.default] : "",
    size.ipad !== undefined ? IPAD_SIZE_CLASSES[size.ipad] : "",
    size.desktop !== undefined ? DESKTOP_SIZE_CLASSES[size.desktop] : "",
  ]
    .filter(Boolean)
    .join(" ")
}

const Body = React.forwardRef<
  HTMLParagraphElement,
  Typography<HTMLParagraphElement>
>(({ className, size = "lg", weight = "regular", ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        COMMON_CLASS_NAME,
        COMMON_BODY_STYLES,
        resolveSizeClasses(size),
        resolveWeightClasses(weight),
        className,
      )}
      {...props}
    />
  )
})
Body.displayName = "Body Text"

const H1 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, size = "6xl", weight = "bold", ...props }, ref) => {
    return (
      <h1
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          resolveSizeClasses(size),
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H1.displayName = "H1 Title Text"

const H2 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, size = "4xl", weight = "bold", ...props }, ref) => {
    return (
      <h2
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          resolveSizeClasses(size),
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H2.displayName = "H2 Title Text"

const H3 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, size = "3xl", weight = "semibold", ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          resolveSizeClasses(size),
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H3.displayName = "H3 Title Text"

const H4 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, size = "3xl", weight = "medium", ...props }, ref) => {
    return (
      <h4
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          resolveSizeClasses(size),
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H4.displayName = "H4 Title Text"

const H5 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, size = "2xl", weight = "semibold", ...props }, ref) => {
    return (
      <h5
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          resolveSizeClasses(size),
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H5.displayName = "H5 Title Text"

const H6 = React.forwardRef<HTMLHeadingElement, Typography<HTMLHeadingElement>>(
  ({ className, weight = "medium", ...props }, ref) => {
    return (
      <h6
        ref={ref}
        className={cn(
          COMMON_CLASS_NAME,
          COMMON_HEADING_STYLES,
          "text-xl",
          resolveWeightClasses(weight),
          className,
        )}
        {...props}
      />
    )
  },
)
H6.displayName = "H6 Title Text"

interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>,
    TypographyVariant {}

const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(({ className, size = "lg", weight = "regular", ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={cn(
      COMMON_CLASS_NAME,
      COMMON_BODY_STYLES,
      resolveSizeClasses(size),
      resolveWeightClasses(weight),
      className,
    )}
    {...props}
  />
))
Label.displayName = LabelPrimitive.Root.displayName

export { H1, H2, H3, H4, H5, H6, Body, Label }
export type { SizeValue, ResponsiveSize, WeightValue, ResponsiveWeight }
